import { toast } from '@/components/toast';
import { useAnalytics } from '@/services/analytics';
import { updateSubscription as _updateSubscription } from '@/services/api';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { isDowngrading } from '@/state/products/Products';
import { PlanGroup } from '@d-id/types';
import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { formattedDate } from './utils';

interface Props {
    mixpanelData: Record<string, any>;
    openPostCheckout: () => void;
    nextBillingDate?: Date;
    newPlan: PlanGroup;
    newPriceCredits: number;
}

export const useUpdateSubscription = ({ mixpanelData, openPostCheckout, nextBillingDate, newPlan, newPriceCredits }: Props) => {
    const { user } = useAuth();
    const mixpanel = useAnalytics();
    const { mutateAsync: updateSubscription, isLoading: isUpdating } = useMutation('update-subscription', _updateSubscription, {
        onError: error => {
            mixpanel?.track('change-plan', { event: 'error', error, ...mixpanelData });
            if ((error as AxiosError<any>)?.response?.data?.kind === 'PlanChangeSupportNeededError') {
                toast.info({ title: "Let's talk. Please contact support to change your plan" });
            } else {
                logger.error('Failed updating subscription', error);
                toast.error({ title: 'Failed to update subscription' });
            }
        },
        onSuccess: () => {
            mixpanel?.track('change-plan', { event: 'success', ...mixpanelData });
            if (
                isDowngrading(
                    user?.planGroup || PlanGroup.TRIAL,
                    parseInt(user?.priceCredits ?? '0'),
                    newPlan as PlanGroup,
                    newPriceCredits
                )
            ) {
                const title = nextBillingDate
                    ? `Plan successfully updated, it will start on ${formattedDate(nextBillingDate)}`
                    : 'Plan successfully updated';
                toast.success({
                    title,
                    options: { autoClose: false },
                });
            } else {
                openPostCheckout();
            }
        },
    });

    return { updateSubscription, isUpdating };
};
